import React, { useState, useMemo } from 'react';
import { clsx } from 'clsx';
import { 
  BookmarkIcon,
  PlusIcon,
  EllipsisVerticalIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  StarIcon,
  EyeIcon,
  LockClosedIcon
} from '@/components/icons';
import { 
  BookmarkSolidIcon,
  StarSolidIcon
} from '@/components/icons';

import type { SavedViewsProps, SavedView } from '@/types/filters';
import { UserRole } from '@/types/api';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';

/**
 * Saved Views Management Component
 * 
 * Provides functionality to:
 * - Save current filter combinations as named views
 * - Load saved views
 * - Manage personal and shared views
 * - Set default views
 * - Duplicate and edit existing views
 */
export function SavedViews({
  views,
  currentViewId,
  currentFilters,
  onSave,
  onLoad,
  onDelete,
  onUpdate,
  onSetDefault,
  onDuplicate,
  loading,
  error,
  className,
  userRole,
  showCreateButton = true,
  showPublicViews = true,
  maxViews = 20
}: SavedViewsProps) {

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [editingView, setEditingView] = useState<SavedView | null>(null);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  
  // Form state
  const [viewName, setViewName] = useState('');
  const [viewDescription, setViewDescription] = useState('');

  // Separate personal and public views
  const { personalViews, publicViews } = useMemo(() => {
    const personal: SavedView[] = [];
    const shared: SavedView[] = [];
    
    views.forEach(view => {
      if (view.createdBy === 'system') {
        shared.push(view);
      } else {
        personal.push(view);
      }
    });
    
    // Sort by usage count and name
    const sortViews = (a: SavedView, b: SavedView) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return (b.usageCount || 0) - (a.usageCount || 0);
    };
    
    return {
      personalViews: personal.sort(sortViews),
      publicViews: shared.sort(sortViews)
    };
  }, [views]);

  // Check if current filters match any existing view
  const hasUnsavedChanges = useMemo(() => {
    if (!currentViewId) return false;
    
    const currentView = views.find(v => v.id === currentViewId);
    if (!currentView) return false;
    
    // Compare current filters with saved view filters
    return JSON.stringify(currentFilters) !== JSON.stringify(currentView.filters);
  }, [currentViewId, currentFilters, views]);

  // Handle save new view
  const handleSaveView = () => {
    if (!viewName.trim()) return;
    
    onSave(viewName.trim(), viewDescription.trim() || undefined, false);
    
    // Reset form
    setViewName('');
    setViewDescription('');
    setShowCreateModal(false);
  };

  // Handle update existing view
  const handleUpdateView = () => {
    if (!editingView || !viewName.trim() || !onUpdate) return;
    
    onUpdate(editingView.id, {
      name: viewName.trim(),
      description: viewDescription.trim() || undefined,
      isPublic: false
    });
    
    setEditingView(null);
    setViewName('');
    setViewDescription('');
  };

  // Handle duplicate view
  const handleDuplicateView = (view: SavedView) => {
    const newName = `${view.name} (Copy)`;
    onDuplicate?.(view.id, newName);
    setMenuOpenId(null);
  };

  // Handle edit view
  const handleEditView = (view: SavedView) => {
    setEditingView(view);
    setViewName(view.name);
    setViewDescription(view.description || '');
    setMenuOpenId(null);
  };

  // Handle delete view
  const handleDeleteView = (viewId: string) => {
    if (confirm('Are you sure you want to delete this saved view?')) {
      onDelete(viewId);
    }
    setMenuOpenId(null);
  };

  // Check if user can edit view
  const canEditView = (view: SavedView) => {
    return view.createdBy !== 'system';
  };

  // Check if we've reached max views
  const hasReachedMaxViews = personalViews.length >= maxViews;

  return (
    <div className={clsx('bg-white', className)}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-900 flex items-center">
            <BookmarkIcon className="w-4 h-4 mr-2 text-gray-400" />
            Saved Views
          </h3>
          {showCreateButton && !hasReachedMaxViews && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="text-xs text-primary-600 hover:text-primary-700 flex items-center"
              title="Save current filters as view"
            >
              <PlusIcon className="w-3 h-3 mr-1" />
              Save
            </button>
          )}
        </div>
        
        {hasUnsavedChanges && (
          <div className="mt-2 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
            You have unsaved changes to the current view
          </div>
        )}
      </div>

      {/* Views List */}
      <div className="max-h-64 overflow-y-auto">
        {/* Personal Views */}
        {personalViews.length > 0 && (
          <div className="p-2">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Saved on this browser
            </div>
            {personalViews.map(view => (
              <div
                key={view.id}
                className={clsx(
                  'group flex items-center justify-between px-3 py-2 rounded-md cursor-pointer transition-colors',
                  currentViewId === view.id
                    ? 'bg-primary-50 text-primary-700'
                    : 'hover:bg-gray-50 text-gray-700'
                )}
              >
                <button
                  onClick={() => onLoad(view.id)}
                  className="flex-1 flex items-center text-left min-w-0"
                >
                  <div className="flex items-center mr-2">
                    {view.isDefault ? (
                      <StarSolidIcon className="w-3 h-3 text-yellow-400 mr-1" />
                    ) : (
                      <BookmarkSolidIcon className={clsx(
                        'w-3 h-3 mr-1',
                        currentViewId === view.id ? 'text-primary-600' : 'text-gray-400'
                      )} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {view.name}
                    </div>
                    {view.description && (
                      <div className="text-xs text-gray-500 truncate">
                        {view.description}
                      </div>
                    )}
                  </div>
                </button>

                {/* Menu */}
                <div className="relative ml-2">
                  <button
                    aria-label={`Actions for ${view.name}`}
                    aria-haspopup="menu"
                    aria-expanded={menuOpenId === view.id}
                    onClick={() => setMenuOpenId(menuOpenId === view.id ? null : view.id)}
                    className={clsx(
                      'p-1 rounded text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100',
                      menuOpenId === view.id && 'opacity-100'
                    )}
                  >
                    <EllipsisVerticalIcon className="w-4 h-4" />
                  </button>
                  
                  {menuOpenId === view.id && (
                    <>
                      <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-20">
                        <div className="py-1">
                          {onDuplicate && <button
                            onClick={() => handleDuplicateView(view)}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                          >
                            <DocumentDuplicateIcon className="w-4 h-4 mr-3" />
                            Duplicate
                          </button>}
                          {canEditView(view) && (
                            <>
                              {onUpdate && <button
                                onClick={() => handleEditView(view)}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                              >
                                <PencilIcon className="w-4 h-4 mr-3" />
                                Edit
                              </button>}
                              <button
                                onClick={() => handleDeleteView(view.id)}
                                className="w-full px-4 py-2 text-left text-sm text-red-700 hover:bg-red-50 flex items-center"
                              >
                                <TrashIcon className="w-4 h-4 mr-3" />
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setMenuOpenId(null)}
                        aria-hidden="true"
                      />
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Public/Shared Views */}
        {showPublicViews && publicViews.length > 0 && (
          <div className="p-2 border-t border-gray-200">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Standard views
            </div>
            {publicViews.map(view => (
              <div
                key={view.id}
                className={clsx(
                  'group flex items-center justify-between px-3 py-2 rounded-md cursor-pointer transition-colors',
                  currentViewId === view.id
                    ? 'bg-primary-50 text-primary-700'
                    : 'hover:bg-gray-50 text-gray-700'
                )}
              >
                <button
                  onClick={() => onLoad(view.id)}
                  className="flex-1 flex items-center text-left min-w-0"
                >
                  <div className="flex items-center mr-2">
                    <EyeIcon className={clsx(
                      'w-3 h-3 mr-1',
                      currentViewId === view.id ? 'text-primary-600' : 'text-gray-400'
                    )} />
                    {view.isDefault && (
                      <StarSolidIcon className="w-3 h-3 text-yellow-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {view.name}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      by {view.createdByName}
                    </div>
                  </div>
                </button>

                {canEditView(view) && (
                  <div className="relative ml-2">
                    <button
                      aria-label={`Actions for ${view.name}`}
                    aria-haspopup="menu"
                    aria-expanded={menuOpenId === view.id}
                    onClick={() => setMenuOpenId(menuOpenId === view.id ? null : view.id)}
                      className="p-1 rounded text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100"
                    >
                      <EllipsisVerticalIcon className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {personalViews.length === 0 && publicViews.length === 0 && (
          <div className="p-6 text-center text-gray-500">
            <BookmarkIcon className="w-8 h-8 mx-auto text-gray-300 mb-2" />
            <div className="text-sm">No saved views</div>
            <div className="text-xs mt-1">Save your filter combinations for quick access</div>
          </div>
        )}

        {/* Max Views Warning */}
        {hasReachedMaxViews && (
          <div className="p-3 bg-amber-50 border-t border-amber-200">
            <div className="text-xs text-amber-800">
              You've reached the maximum of {maxViews} saved views. Delete some views to create new ones.
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit View Modal */}
      <Modal
        isOpen={showCreateModal || editingView !== null}
        onClose={() => {
          setShowCreateModal(false);
          setEditingView(null);
          setViewName('');
          setViewDescription('');
        }}
        title={editingView ? 'Edit Saved View' : 'Save Current View'}
        size="md"
      >
        <div className="space-y-4">
          <Input
            label="View Name"
            value={viewName}
            onChange={(event) => setViewName(event.target.value)}
            placeholder="Enter a name for this view"
            required
            autoFocus
          />
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (optional)
            </label>
            <textarea
              value={viewDescription}
              onChange={(e) => setViewDescription(e.target.value)}
              placeholder="Describe what this view shows"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
          </div>

          <p className="text-sm text-gray-500">This view is saved on this browser.</p>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
              {error}
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-end space-x-3">
          <Button
            variant="outline"
            onClick={() => {
              setShowCreateModal(false);
              setEditingView(null);
              setViewName('');
              setViewDescription('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={editingView ? handleUpdateView : handleSaveView}
            disabled={!viewName.trim() || loading}
            loading={loading}
          >
            {editingView ? 'Update View' : 'Save View'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
